import { useEffect, useRef, useState } from 'react';

// Anima un número desde su valor anterior (0 al montar) hasta el valor final
// (~700ms, easeOutCubic) usando requestAnimationFrame. Para los "count-up" de
// los widgets del dashboard: el componente recibe el número crudo animado y lo
// formatea él mismo (Q, decimales, redondeo).
//
// Respeta prefers-reduced-motion: si el sistema operativo pide menos
// movimiento, salta directo al valor final sin animar.
//
// Uso:
//   const pedidosAnimado = useConteoAnimado(estadisticas?.pedidosValidos || 0);
//   ... {Math.round(pedidosAnimado)}
// Tip: al número que se anima conviene ponerle fontVariantNumeric:
// 'tabular-nums' para que no "baile" el ancho mientras cuenta.
export default function useConteoAnimado(valorFinal, duracionMs = 700) {
  const objetivo = Number(valorFinal) || 0;
  const [valor, setValor] = useState(0);
  const previoRef = useRef(0);

  useEffect(() => {
    const reducirMovimiento = typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reducirMovimiento || duracionMs <= 0) {
      previoRef.current = objetivo;
      setValor(objetivo);
      return;
    }

    const desde = previoRef.current;
    if (desde === objetivo) {
      setValor(objetivo);
      return;
    }

    let rafId;
    const inicio = performance.now();
    const paso = (ahora) => {
      const t = Math.min(1, (ahora - inicio) / duracionMs);
      const suavizado = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setValor(desde + (objetivo - desde) * suavizado);
      if (t < 1) {
        rafId = requestAnimationFrame(paso);
      } else {
        previoRef.current = objetivo;
      }
    };
    rafId = requestAnimationFrame(paso);
    return () => cancelAnimationFrame(rafId);
  }, [objetivo, duracionMs]);

  return valor;
}
